<script setup lang="ts">
import { convert } from 'html-to-text';
import { useOnline } from '@vueuse/core';
import type { QuizAnswerState } from '~/types/quiz.types';
import { TransitionName } from "~/enums/transition.enums";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { QuizType } from "~/enums/quiz.enums";
import { theme } from "~/tailwind.config";

const {
  quiz,
  currentQuestion,
  setCurrentQuestionAnswer,
  answersValidate,
  progressAdvance,
} = useQuizStore();
const { t } = useI18n();
const { between } = useScreen();

const isKickoffQuiz = computed(() => quiz.value?.type === QuizType.Kickoff);

const stepIndex = ref(0);
const transition = ref<TransitionName.SlideLeft | TransitionName.SlideRight>(TransitionName.SlideLeft);
const isOnline = useOnline();
const isVideoEnded = ref(false);
const isShowingExpertVideo = ref(false);

const steps = computed(() => {
  const steps = [
    'question',
  ];

  if (currentQuestion.value.question?.preQuestionVideo) {
    steps.unshift('video');
  }
  if (currentQuestion.value.question.postQuestionVideo) {
    steps.push('out-video');
  }
  return steps;
});

const step = computed(() => {
  return steps.value[stepIndex.value];
});

const prevStep = () => {
  if (stepIndex.value > 0) {
    stepIndex.value--;
    transition.value = TransitionName.SlideRight;
    isVideoEnded.value = false;
  }
};

const nextStep = () => {
  isVideoEnded.value = false;

  if (stepIndex.value === steps.value.length - 1) {
    progressAdvance(remainingQuestions.value);
    stepIndex.value = 0;
    return;
  } else {
    transition.value = TransitionName.SlideLeft;
    stepIndex.value++;
  }
};

const remainingQuestions = ref();

const footerElement = ref();
const questionElement = ref();

const validateAnswers = async () => {
  if (!currentQuestion.value.isRevealingAnswer) {
    remainingQuestions.value = await answersValidate();
    footerElement.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  } else {
    nextStep();
    questionElement.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
};

const onVideoEnded = () => {
  isVideoEnded .value = true;
};

const questionText = computed(() => {
  // todo: replace with dompurify to sanitize html, but still be able to have rich text editing displayed?
  return convert(currentQuestion.value.question.question, {
    decodeEntities: true,
  });
});

const isSubmittable = computed(() => {
  return isOnline.value && currentQuestion.value.selectedAnswers.length;
});

const expertAnswers = computed(() => {
  return currentQuestion.value.question.answers.map(answer => {
    return {
      id: answer.id,
      // todo: replace with dompurify to sanitize html, but still be able to have rich text editing displayed?
      answer: convert((answer?.answer || answer?.title || ''), {
        decodeEntities: true,
      }),
      isEnabled: false,
      isSelected: currentQuestion.value.selectedAnswers.map(i => i.id).includes(answer.id),
      isCorrect: currentQuestion.value.isRevealingAnswer && currentQuestion.value.correctAnswers.includes(answer.id),
      isRevealing: currentQuestion.value.isRevealingAnswer,
    } as QuizAnswerState;
  });
});

onMounted(() => {
  questionElement.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
});
</script>

<template>
  <div
    :class="[
      'flex flex-col items-center',
      'md:overflow-y-scroll no-scrollbar',
      'md:max-h-[calc(100svh-72px)] md-h:max-h-[calc(100svh-50px)] h-full'
    ]"
  >
    <div
      v-if="step === 'video' || step === 'out-video'"
      class="flex flex-col gap-4 h-full md:p-4 md-h:py-0 md-h-reverse:max-w-screen-xl w-full"
      :class="[
        {'md-h:flex-row md-h:h-full md-h:justify-between': !between(0, parseInt(theme.screens.md))}
      ]"
    >
      <div
        v-if="!between(0, parseInt(theme.screens.md))"
        class="md-h:block hidden"
      >
        <Button class="opacity-0">
          {{t('global.button.continue')}}
        </Button>
      </div>

      <div class="relative flex flex-col items-center justify-center aspect-video overflow-hidden">
        <LessonPlayer
          :vimeo-id="step === 'video' ? currentQuestion.question.preQuestionVideo?.source! : currentQuestion.question.postQuestionVideo?.source!"
          class="md-h:rounded-none"
          @ended="onVideoEnded"
        />
      </div>

      <div class="flex items-end justify-end md:pr-0 pr-4 md-h:pb-4">
        <Button
          :severity="isVideoEnded ? 'primary' : 'secondary'"
          :class="isVideoEnded ? 'primary' : 'bg-neutral-10 text-neutral-80'"
          class="border-none"
          @click="nextStep()"
        >
          {{t('global.button.continue')}}
        </Button>
      </div>
    </div>

    <div
      v-if="step === 'question' || step === 'answers'"
      class="w-full h-full sm:grid grid-cols-2 flex flex-col"
    >
      <div class="flex lg:justify-end p-6 bg-white">
        <div
          ref="questionElement"
          class="flex flex-col gap-4 lg:max-w-[600px] w-full"
        >
          <div class="text-20 text-neutral-90 font-bold">
            {{questionText}}
          </div>

          <QuizMedia
            v-if="currentQuestion.question.questionMedia"
            :key="currentQuestion.questionId"
            :media="currentQuestion.question.questionMedia"
            :video-loop-enabled="currentQuestion?.question?.videoLoopEnabled"
          />
        </div>
      </div>

      <div class="flex lg:justify-start p-6 pb-0 bg-neutral-10">
        <div class="flex flex-col lg:max-w-[600px] w-full pb-[env(safe-area-inset-bottom)]">
          <QuizAnswers
            :current-question="currentQuestion"
            @change="setCurrentQuestionAnswer($event);"
          />

          <!-- fixme: should be inside QuizAnswers component -->
          <div
            v-if="currentQuestion.isRevealingAnswer"
            class="text-14 font-bold text-center text-white rounded-lg mt-6 py-1 px-4 uppercase"
            :class="currentQuestion.isCorrect ? 'bg-green-60' : 'bg-red-50'"
          >
            {{currentQuestion.isCorrect ? t('course.quiz.question.answer.correct') : t('course.quiz.question.answer.incorrect')}}
          </div>

          <div
            ref="footerElement"
            class="flex flex-wrap gap-6 justify-end w-full py-6"
          >
            <Button
              v-if="steps.includes('video')"
              severity="secondary"
              @click="prevStep"
            >
              {{t('course.quiz.question.button.backToVideo')}}
            </Button>

            <Button
              v-if="currentQuestion.isRevealingAnswer && currentQuestion.question.feedbackVideo && !isKickoffQuiz"
              outlined
              @click="isShowingExpertVideo = true"
            >
              {{t('course.quiz.question.button.watchAnExpertAnswer')}}
            </Button>

            <Button
              :disabled="!isSubmittable"
              :loading="currentQuestion.isValidating"
              @click="validateAnswers"
            >
              <FontAwesomeIcon
                v-if="currentQuestion.isValidating"
                icon="fa-circle-notch"
                class="animate-spin"
              />
              <span v-else>
                {{!currentQuestion.isRevealingAnswer ? t('course.quiz.question.button.validate') : t('global.button.continue')}}
              </span>
            </Button>
          </div>

          <QuizExpertAnswerModal
            v-model="isShowingExpertVideo"
            :question-text="questionText"
            :type="currentQuestion.question.type"
            :feedback-video="currentQuestion.question.feedbackVideo"
            :is-question-correct="currentQuestion.isCorrect"
            :answers="expertAnswers"
          />
        </div>
      </div>
    </div>
  </div>
</template>
